//@flow

import {ActionCreator, createReducer} from '../../store/utils';

import type {
  EInvoiceProvider,
  TaxInvoice,
  TaxInvoiceDocument,
  Pagination,
  StoreTaxInvoiceType,
  TaxInvoiceSetting,
} from '../../entities';

type TaxInvoiceTableFilter = {
  pagination?: Pagination,
  query?: string,
  endDate?: string,
  startDate?: string,
};

export type EInvoiceState = {
  providers: EInvoiceProvider[],
  storeProviders: TaxInvoice[],
  isFetchingProviders: boolean,
  isFetchingInvoiceByProvider: boolean,
  isFetchingStoreProviders: boolean,
  isFetchingInvoiceTypes: boolean,

  storeTaxInvoiceType?: StoreTaxInvoiceType[],

  totalOrders?: number,
  orderTaxInvoiceList?: TaxInvoiceDocument[],
  tableFilters: TaxInvoiceTableFilter,

  taxInvoiceSetting?: TaxInvoiceSetting,
};

export type EInvoiceAction = {
  providers?: EInvoiceProvider[],
  taxInvoiceSetting?: TaxInvoiceSetting,
  storeProviders?: TaxInvoice[],
  isFetchingProviders?: boolean,
  isFetchingInvoiceByProvider?: boolean,
  isFetchingStoreProviders?: boolean,
  isFetchingInvoiceTypes?: boolean,
  orderTaxInvoiceList?: TaxInvoiceDocument[],
  tableFilters?: TaxInvoiceTableFilter,
  storeTaxInvoiceType?: StoreTaxInvoiceType[],
  totalOrders?: number,
};

export const initialState: EInvoiceState = {
  providers: [],
  storeProviders: [],
  isFetchingProviders: false,
  isFetchingInvoiceByProvider: false,
  isFetchingStoreProviders: false,
  isFetchingInvoiceTypes: false,

  tableFilters: {
    pagination: {
      size: 10,
      page: 0,
      total: 0,
    },
  },
};

export class SetProviders extends ActionCreator<EInvoiceAction> {}
export class SetStoreProviders extends ActionCreator<EInvoiceAction> {}

export class SetFetchingProviders extends ActionCreator<EInvoiceAction> {}
export class SetFetchingInvoiceByProvider extends ActionCreator<EInvoiceAction> {}
export class SetFetchingStoreProviders extends ActionCreator<EInvoiceAction> {}

export class SetOrderTaxInvoiceList extends ActionCreator<EInvoiceAction> {}
export class SetTableFilters extends ActionCreator<EInvoiceAction> {}
export class ResetTableFilters extends ActionCreator<EInvoiceAction> {}
export class SetTotalOrders extends ActionCreator<EInvoiceAction> {}

export class SetStoreTaxInvoiceType extends ActionCreator<EInvoiceAction> {}

export class SetTaxInvoiceSetting extends ActionCreator<EInvoiceAction> {}

export const actions = {
  setProviders: (providers: EInvoiceProvider[]) =>
    new SetProviders({providers}),
  setStoreProviders: (storeProviders: TaxInvoice[]) =>
    new SetStoreProviders({storeProviders}),
  setFetchingProviders: (isFetchingProviders: boolean) =>
    new SetFetchingProviders({isFetchingProviders}),
  setFetchingInvoiceByProvider: (isFetchingInvoiceByProvider: boolean) =>
    new SetFetchingInvoiceByProvider({isFetchingInvoiceByProvider}),
  setFetchingStoreProviders: (isFetchingStoreProviders: boolean) =>
    new SetFetchingStoreProviders({isFetchingStoreProviders}),

  setOrderTaxInvoiceList: (orderTaxInvoiceList: TaxInvoiceDocument[]) =>
    new SetOrderTaxInvoiceList({orderTaxInvoiceList}),

  setStoreTaxInvoiceType: (storeTaxInvoiceType: StoreTaxInvoiceType[]) =>
    new SetStoreTaxInvoiceType({storeTaxInvoiceType}),

  setTablePagination: (pagination: Pagination) =>
    new SetTableFilters({
      tableFilters: {
        pagination,
      },
    }),

  setTableQuery: (query: string) =>
    new SetTableFilters({
      tableFilters: {
        query,
      },
    }),

  setTableStartDate: (startDate: string) =>
    new SetTableFilters({
      tableFilters: {
        startDate,
      },
    }),

  setTableEndDate: (endDate: string) =>
    new SetTableFilters({
      tableFilters: {
        endDate,
      },
    }),

  resetTableFilters: () => new ResetTableFilters(),

  setTotalOrders: (totalOrders: number) => new SetTotalOrders({totalOrders}),

  setTaxInvoiceSetting: (taxInvoiceSetting: TaxInvoiceSetting) =>
    new SetTaxInvoiceSetting({taxInvoiceSetting}),
};

export default createReducer(initialState, {
  [SetProviders.type]: (state, {providers}: EInvoiceAction): EInvoiceState => ({
    ...state,
    providers,
  }),

  [SetStoreProviders.type]: (
    state,
    {storeProviders}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    storeProviders,
  }),

  [SetFetchingProviders.type]: (
    state,
    {isFetchingProviders}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    isFetchingProviders,
  }),

  [SetFetchingInvoiceByProvider.type]: (
    state,
    {isFetchingInvoiceByProvider}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    isFetchingInvoiceByProvider,
  }),

  [SetFetchingStoreProviders.type]: (
    state,
    {isFetchingStoreProviders}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    isFetchingStoreProviders,
  }),

  [SetOrderTaxInvoiceList.type]: (
    state,
    {orderTaxInvoiceList}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    orderTaxInvoiceList,
  }),

  [SetTableFilters.type]: (
    state,
    {tableFilters}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    tableFilters: {
      ...state.tableFilters,
      ...tableFilters,
    },
  }),
  [ResetTableFilters.type]: (state): EInvoiceState => ({
    ...state,
    tableFilters: initialState.tableFilters,
  }),

  [SetTotalOrders.type]: (
    state,
    {totalOrders}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    totalOrders,
  }),

  [SetStoreTaxInvoiceType.type]: (
    state,
    {storeTaxInvoiceType}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    storeTaxInvoiceType,
  }),

  [SetTaxInvoiceSetting.type]: (
    state,
    {taxInvoiceSetting}: EInvoiceAction
  ): EInvoiceState => ({
    ...state,
    taxInvoiceSetting,
  }),
});
